import { useFormik } from 'formik';
import { useQueryClient } from 'react-query';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useNotifications } from 'modules/app/notifications';
import { useClearSpainClosureErrors } from 'modules/bookkeep/hooks/api';
import {
  useInvalidateGetPayableCountQueryCache,
  useInvalidateGetPayableListQueryCache,
} from 'modules/payable/hooks';
import {
  VAT_NUMBER_MIN_LENGTH,
  VAT_NUMBER_MAX_LENGTH,
} from 'modules/suppliers/components/SupplierSpanishDprComplianceForm/VatNumberFormField/VatNumberFormField';
import { useUser } from 'src/core/modules/app/hooks/useUser';

import { useCreateSupplier, useUpdateSupplier } from '../api';

type Props = {
  supplier?: {
    id: string;
    name: string;
    country?: string;
    vatNumber?: string;
  };
  onSupplierSelected: (
    supplier: { id: string; name: string },
    confirmRightAfter?: boolean,
  ) => void;
  shouldDisplayDPRInformations: boolean | undefined;
  shouldClearSpainClosureErrors?: boolean;
  onSuccess?: () => void;
};

export const useSupplierEditForm = ({
  supplier,
  onSupplierSelected,
  shouldDisplayDPRInformations,
  shouldClearSpainClosureErrors,
  onSuccess,
}: Props) => {
  const { t } = useTranslation('global');
  const { successNotif } = useNotifications();
  const companyId = useCompanyId();
  const user = useUser();
  const queryClient = useQueryClient();
  const { clearSpainClosureErrors } = useClearSpainClosureErrors();
  const invalidatePayableListQueryCache =
    useInvalidateGetPayableListQueryCache();
  const invalidatePayableCountQueryCache =
    useInvalidateGetPayableCountQueryCache();

  const [
    createSupplier,
    createSupplierQueryState,
    resetCreateSupplierQueryState,
  ] = useCreateSupplier();

  const [
    updateSupplier,
    updateSupplierQueryState,
    resetUpdateSupplierQueryState,
  ] = useUpdateSupplier(supplier?.id ?? '', {});

  const resetQueryStates = () => {
    resetCreateSupplierQueryState();
    resetUpdateSupplierQueryState();
  };

  const clearClosureErrorsAndInvalidate = async (supplierId: string) => {
    const { updatedPayableIds } = await clearSpainClosureErrors({
      supplierId,
    });

    await Promise.all([
      invalidatePayableListQueryCache(companyId),
      invalidatePayableCountQueryCache(companyId),
      ...updatedPayableIds.flatMap((payableId) => [
        queryClient.invalidateQueries([`payablePanel::${payableId}`]),
        queryClient.invalidateQueries([
          'getPayableForAllPayables',
          companyId,
          payableId,
        ]),
      ]),
    ]);

    return updatedPayableIds;
  };

  const notifyBulkUpdateResult = (updatedPayableIds: string[]) => {
    const updatedPayablesCount = updatedPayableIds.length;
    if (updatedPayablesCount > 0) {
      const messageKey =
        updatedPayablesCount === 1
          ? 'suppliers.bulkUpdateNotification.single'
          : 'suppliers.bulkUpdateNotification.multiple';
      successNotif(
        t(messageKey, {
          count: updatedPayablesCount,
        }),
      );
      return;
    }

    successNotif(t('suppliers.updateSuccess'));
  };

  const updateExistingSupplier = async (values: {
    vatNumber?: string;
    country?: string;
  }) => {
    if (!supplier?.id) {
      return;
    }

    const hasVatNumberChanged = values.vatNumber !== supplier.vatNumber;
    const hasCountryChanged = values.country !== supplier.country;

    if (!hasVatNumberChanged && !hasCountryChanged) {
      return;
    }

    await updateSupplier({
      id: supplier.id,
      address: { country: values.country },
      legalInfos: {
        vatNumber: values.vatNumber,
      },
    });

    if (shouldDisplayDPRInformations && shouldClearSpainClosureErrors) {
      const updatedPayableIds = await clearClosureErrorsAndInvalidate(
        supplier.id,
      );
      notifyBulkUpdateResult(updatedPayableIds);
      return;
    }

    if (hasVatNumberChanged) {
      successNotif(t('suppliers.taxIdUpdateSuccess'));
      return;
    }

    successNotif(t('suppliers.updateSuccess'));
  };

  const formikProps = useFormik<{
    newSupplierName?: string;
    vatNumber?: string;
    country?: string;
  }>({
    initialValues: {
      vatNumber: supplier?.vatNumber,
      country: supplier?.country,
      newSupplierName: undefined,
    },
    onSubmit: async (values) => {
      resetQueryStates();
      try {
        if (values.newSupplierName?.length) {
          // bypass invoice supplier creation for requester only users. Move to payment update flow.
          if (!user.is_account_owner && !user.is_controller) {
            onSupplierSelected(
              {
                id: '',
                name: values.newSupplierName,
              },
              true,
            );
            return;
          }

          const newSupplier = await createSupplier({
            name: values.newSupplierName,
            address: { country: values.country },
            legalInfos: {
              legalName: values.newSupplierName,
              vatNumber: values.vatNumber,
            },
            isArchived: false,
            isSupplierValidated: false,
          });
          onSupplierSelected(newSupplier, true);

          return;
        }

        await updateExistingSupplier(values);

        onSuccess?.();
      } catch {
        // handled by the query state
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    validate: async (values) => {
      if (!supplier && !values.newSupplierName) {
        return { newSupplierName: 'required' };
      }
      if (shouldDisplayDPRInformations) {
        const errors: { country?: string; vatNumber?: string } = {};

        if (!values.country?.length) {
          errors.country = 'required';
        }

        if (!values.vatNumber?.length) {
          errors.vatNumber = 'required';
        } else if (
          values.vatNumber.length < VAT_NUMBER_MIN_LENGTH ||
          values.vatNumber.length > VAT_NUMBER_MAX_LENGTH
        ) {
          errors.vatNumber = 'invalidLength';
        }

        if (Object.keys(errors).length > 0) {
          return errors;
        }
      }
    },
  });

  return {
    formikProps: {
      ...formikProps,
      errors: formikProps.errors,
    },
    createSupplierQueryState,
    updateSupplierQueryState,
  };
};
